// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { IlliquidAssetEvent } from '@shared/data-access/types/engine-types';
import { Translation } from '@shared/ui-kit/providers/translation-provider';
import { classNames } from '@shared/ui-kit/utils/classnames';
import { Typography } from '@mui/material';
import { MIN_ILLIQUID_VALUATION_CHART_POINTS } from '../../utils/build-illiquid-valuation-chart-points';
import { useIlliquidAssetValueEvolutionChart } from './use-illiquid-asset-value-evolution-chart';
import styles from './illiquid-asset-value-evolution-section.module.css';

export type IlliquidAssetValueEvolutionSectionProps = {
  readonly events: readonly IlliquidAssetEvent[] | undefined;

  readonly currency: string;
};

/**
 * Value-evolution area chart for illiquid valuation events.
 *
 * @remarks
 * Renders only when there are at least {@link MIN_ILLIQUID_VALUATION_CHART_POINTS}
 * valuation points. Series times use `created_at` so same-day valuations do not
 * collide.
 */
export const IlliquidAssetValueEvolutionSection = ({
  events,
  currency,
}: IlliquidAssetValueEvolutionSectionProps) => {
  const { points, chartContainerRef, tooltipRef, tooltipValues } =
    useIlliquidAssetValueEvolutionChart({ events, currency });

  if (points.length < MIN_ILLIQUID_VALUATION_CHART_POINTS) {
    return null;
  }

  return (
    <section
      className={styles.section}
      data-testid="illiquid-asset-value-evolution-section"
    >
      <Typography component="h3" className={styles['section-title']}>
        <Translation
          i18nKey="illiquid-asset-drawer.section.value-evolution"
          ns="components"
        />
      </Typography>

      <div
        ref={chartContainerRef}
        className={styles.chart}
        data-testid="illiquid-asset-value-evolution-chart"
      >
        {tooltipValues?.date && (
          <div ref={tooltipRef} className={styles.tooltip}>
            <p className={styles['tooltip-date']}>{tooltipValues.date}</p>

            <p className={classNames(styles['tooltip-value'], 'money-display')}>
              {tooltipValues.value}
            </p>
          </div>
        )}
      </div>
    </section>
  );
};
